import React, { useCallback, useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { Crown, LogOut, Calendar, DollarSign, Clock, CheckCircle2, Loader2 } from "lucide-react";
import { useAuth } from "@/lib/auth";
import apiClient, { SERVICES_META } from "@/lib/api";
import { toast } from "sonner";

const STATUS_META = {
  pending: { label: "Pending", className: "text-yellow-400" },
  confirmed: { label: "Confirmed", className: "text-[#D4AF37]" },
  completed: { label: "Completed", className: "text-green-400" },
  cancelled: { label: "Cancelled", className: "text-zinc-500" },
};

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [promos, setPromos] = useState([]);
  const [busy, setBusy] = useState(false);

  const loadAll = useCallback(async () => {
    try {
      const [s, b, t, r, p] = await Promise.all([
        apiClient.get("/admin/stats"),
        apiClient.get("/admin/bookings"),
        apiClient.get("/admin/travel-quotes"),
        apiClient.get("/admin/reviews"),
        apiClient.get("/admin/promo-codes"),
      ]);
      setStats(s.data);
      setBookings(b.data);
      setQuotes(t.data);
      setReviews(r.data);
      setPromos(p.data);
    } catch (e) {
      toast.error("Failed to load dashboard");
    }
  }, []);

  useEffect(() => {
    if (user) loadAll();
  }, [user, loadAll]);

  if (loading) return null;
  if (!user) return <Navigate to="/admin/login" replace />;

  const updateStatus = async (id, status) => {
    setBusy(true);
    try {
      await apiClient.patch(`/admin/bookings/${id}`, { status });
      await loadAll();
      toast.success("Booking updated");
    } catch (e) {
      toast.error("Update failed");
    } finally {
      setBusy(false);
    }
  };

  const updateReview = async (id, status) => {
    try {
      await apiClient.patch(`/admin/reviews/${id}`, { status });
      await loadAll();
      toast.success(`Review ${status}`);
    } catch (e) {
      toast.error("Review update failed");
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="admin-dashboard-page">
      <header className="border-b border-[#D4AF37]/15 px-6 md:px-12 lg:px-24 py-5 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3">
          <Crown className="w-6 h-6 text-[#D4AF37]" />
          <div>
            <span className="font-serif text-lg block leading-none">
              <span className="text-white">Luxury</span>{" "}
              <span className="gold-gradient-text font-medium">Kingz</span>
            </span>
            <span className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Admin Console</span>
          </div>
        </Link>
        <div className="flex items-center gap-6">
          <span className="text-xs text-zinc-500 hidden sm:inline" data-testid="admin-user-email">
            {user.email}
          </span>
          <button onClick={logout} data-testid="admin-logout" className="text-xs uppercase tracking-widest text-zinc-400 hover:text-[#D4AF37] flex items-center gap-2">
            <LogOut className="w-4 h-4" /> Sign Out
          </button>
        </div>
      </header>

      <div className="px-6 md:px-12 lg:px-24 py-10">
        <h1 className="font-serif text-4xl md:text-5xl font-light mb-2">
          Dashboard
        </h1>
        <p className="text-zinc-400 mb-10">A glance at the empire.</p>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mb-12" data-testid="admin-stats">
          <StatCard icon={Calendar} label="Total Bookings" value={stats?.total_bookings ?? "—"} testid="stat-total" />
          <StatCard icon={Clock} label="Pending" value={stats?.pending_bookings ?? "—"} testid="stat-pending" />
          <StatCard icon={CheckCircle2} label="Confirmed" value={stats?.confirmed_bookings ?? "—"} testid="stat-confirmed" />
          <StatCard icon={DollarSign} label="Revenue" value={stats ? `$${stats.total_revenue.toFixed(2)}` : "—"} testid="stat-revenue" />
        </div>

        {/* Bookings */}
        <div className="lk-card overflow-hidden" data-testid="admin-bookings-table">
          <div className="px-6 py-5 border-b border-[#D4AF37]/15 flex items-center justify-between">
            <h2 className="font-serif text-2xl">Bookings</h2>
            <button onClick={loadAll} className="text-xs uppercase tracking-widest text-[#D4AF37] hover:text-[#F3E5AB]">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Refresh"}
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-widest text-zinc-500 border-b border-[#D4AF37]/10">
                <tr>
                  <th className="text-left p-4">Customer</th>
                  <th className="text-left p-4">Service</th>
                  <th className="text-left p-4">Date</th>
                  <th className="text-left p-4">Hours</th>
                  <th className="text-right p-4">Total</th>
                  <th className="text-left p-4">Payment</th>
                  <th className="text-left p-4">Status</th>
                  <th className="text-right p-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {bookings.length === 0 && (
                  <tr>
                    <td colSpan={8} className="p-10 text-center text-zinc-500">
                      No bookings yet.
                    </td>
                  </tr>
                )}
                {bookings.map((b) => {
                  const s = STATUS_META[b.status] || STATUS_META.pending;
                  return (
                    <tr key={b.id} className="border-b border-[#D4AF37]/5 hover:bg-[#D4AF37]/[0.02]" data-testid={`booking-row-${b.id}`}>
                      <td className="p-4">
                        <Link to={`/admin/bookings/${b.id}`} className="text-white hover:text-[#D4AF37] transition-colors" data-testid={`booking-detail-link-${b.id}`}>
                          {b.full_name}
                        </Link>
                        <div className="text-xs text-zinc-500">{b.email}</div>
                        <div className="text-xs text-zinc-500">{b.phone}</div>
                      </td>
                      <td className="p-4 text-zinc-300">{SERVICES_META[b.service]?.title || b.service}</td>
                      <td className="p-4 text-zinc-300">
                        {b.event_date}
                        <div className="text-xs text-zinc-500">{b.start_time}</div>
                      </td>
                      <td className="p-4 text-zinc-300">{b.hours}h</td>
                      <td className="p-4 text-right gold-gradient-text font-serif">${b.total_amount.toFixed(2)}</td>
                      <td className="p-4">
                        <span className={`text-xs uppercase tracking-widest ${b.payment_status === "paid" ? "text-green-400" : "text-zinc-500"}`}>
                          {b.balance_paid ? "fully paid" : b.payment_status === "paid" ? "deposit paid" : b.payment_status}
                        </span>
                      </td>
                      <td className="p-4">
                        <span className={`text-xs uppercase tracking-widest ${s.className}`}>{s.label}</span>
                      </td>
                      <td className="p-4 text-right">
                        <select
                          value={b.status}
                          onChange={(e) => updateStatus(b.id, e.target.value)}
                          className="bg-transparent border border-[#D4AF37]/30 text-xs px-3 py-1.5 focus:outline-none focus:border-[#D4AF37]"
                          data-testid={`status-select-${b.id}`}
                        >
                          <option value="pending">Pending</option>
                          <option value="confirmed">Confirmed</option>
                          <option value="completed">Completed</option>
                          <option value="cancelled">Cancelled</option>
                        </select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="lk-card overflow-hidden mt-12" data-testid="admin-travel-quotes">
          <div className="px-6 py-5 border-b border-[#D4AF37]/15">
            <h2 className="font-serif text-2xl">Travel Quote Requests</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-widest text-zinc-500 border-b border-[#D4AF37]/10">
                <tr>
                  <th className="text-left p-4">Name</th>
                  <th className="text-left p-4">Destination</th>
                  <th className="text-left p-4">Ideal Budget</th>
                  <th className="text-left p-4">Contact</th>
                  <th className="text-left p-4">Received</th>
                </tr>
              </thead>
              <tbody>
                {quotes.length === 0 && (
                  <tr>
                    <td colSpan={5} className="p-10 text-center text-zinc-500">
                      No travel quote requests yet.
                    </td>
                  </tr>
                )}
                {quotes.map((q) => (
                  <tr key={q.id} className="border-b border-[#D4AF37]/5 hover:bg-[#D4AF37]/[0.02]" data-testid={`quote-row-${q.id}`}>
                    <td className="p-4 text-white">{q.name}</td>
                    <td className="p-4 text-zinc-300">{q.destination}</td>
                    <td className="p-4 gold-gradient-text font-serif">{q.budget}</td>
                    <td className="p-4">
                      <div className="text-xs text-zinc-400">{q.email}</div>
                      <div className="text-xs text-zinc-500">{q.phone}</div>
                    </td>
                    <td className="p-4 text-xs text-zinc-500">{new Date(q.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="lk-card overflow-hidden mt-12" data-testid="admin-reviews">
          <div className="px-6 py-5 border-b border-[#D4AF37]/15">
            <h2 className="font-serif text-2xl">Reviews</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-widest text-zinc-500 border-b border-[#D4AF37]/10">
                <tr>
                  <th className="text-left p-4">Name</th>
                  <th className="text-left p-4">Event</th>
                  <th className="text-left p-4">Rating</th>
                  <th className="text-left p-4">Review</th>
                  <th className="text-left p-4">Status</th>
                  <th className="text-right p-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {reviews.length === 0 && (
                  <tr>
                    <td colSpan={6} className="p-10 text-center text-zinc-500">
                      No reviews yet.
                    </td>
                  </tr>
                )}
                {reviews.map((r) => (
                  <tr key={r.id} className="border-b border-[#D4AF37]/5 hover:bg-[#D4AF37]/[0.02]" data-testid={`review-row-${r.id}`}>
                    <td className="p-4 text-white whitespace-nowrap">{r.name}</td>
                    <td className="p-4 text-zinc-300 whitespace-nowrap">{r.event_type}</td>
                    <td className="p-4 gold-gradient-text whitespace-nowrap">{"★".repeat(r.rating)}</td>
                    <td className="p-4 text-zinc-400 max-w-md">
                      <span className="line-clamp-2">{r.text}</span>
                    </td>
                    <td className="p-4">
                      <span className={`text-xs uppercase tracking-widest ${r.status === "approved" ? "text-green-400" : r.status === "pending" ? "text-yellow-400" : "text-zinc-500"}`}>
                        {r.status}
                      </span>
                    </td>
                    <td className="p-4 text-right whitespace-nowrap">
                      {r.status !== "approved" && (
                        <button
                          onClick={() => updateReview(r.id, "approved")}
                          className="text-xs uppercase tracking-widest text-green-400 hover:text-green-300 mr-4"
                          data-testid={`approve-review-${r.id}`}
                        >
                          Approve
                        </button>
                      )}
                      {r.status !== "rejected" && (
                        <button
                          onClick={() => updateReview(r.id, "rejected")}
                          className="text-xs uppercase tracking-widest text-zinc-500 hover:text-red-400"
                          data-testid={`reject-review-${r.id}`}
                        >
                          Reject
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <PromoCodesCard promos={promos} reload={loadAll} />
      </div>
    </div>
  );
}

function PromoCodesCard({ promos, reload }) {
  const [form, setForm] = useState({ code: "", discount_type: "percent", value: "", expires_at: "", max_uses: "" });
  const [creating, setCreating] = useState(false);

  const create = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await apiClient.post("/admin/promo-codes", {
        code: form.code.trim().toUpperCase(),
        discount_type: form.discount_type,
        value: parseFloat(form.value),
        expires_at: form.expires_at || null,
        max_uses: form.max_uses ? parseInt(form.max_uses, 10) : null,
      });
      setForm({ code: "", discount_type: "percent", value: "", expires_at: "", max_uses: "" });
      await reload();
      toast.success("Promo code created");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not create code");
    } finally {
      setCreating(false);
    }
  };

  const toggle = async (p) => {
    try {
      await apiClient.patch(`/admin/promo-codes/${p.id}`, { active: !p.active });
      await reload();
    } catch (e) {
      toast.error("Update failed");
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete promo code ${p.code}?`)) return;
    try {
      await apiClient.delete(`/admin/promo-codes/${p.id}`);
      await reload();
      toast.success("Deleted");
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="lk-card overflow-hidden mt-12" data-testid="admin-promo-codes">
      <div className="px-6 py-5 border-b border-[#D4AF37]/15">
        <h2 className="font-serif text-2xl">Promo Codes</h2>
      </div>
      <form onSubmit={create} className="p-6 grid grid-cols-2 md:grid-cols-6 gap-4 border-b border-[#D4AF37]/10 items-end">
        <div className="col-span-2 md:col-span-1">
          <label className="text-[10px] uppercase tracking-widest text-[#D4AF37] mb-1.5 block">Code</label>
          <input type="text" required minLength={3} value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))} className="lk-input text-sm" placeholder="GOLD20" data-testid="promo-code-input" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-[#D4AF37] mb-1.5 block">Type</label>
          <select value={form.discount_type} onChange={(e) => setForm((f) => ({ ...f, discount_type: e.target.value }))} className="lk-input text-sm bg-[#050505]" data-testid="promo-type-select">
            <option value="percent">% off</option>
            <option value="fixed">$ off</option>
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-[#D4AF37] mb-1.5 block">Value</label>
          <input type="number" required min="0.01" step="0.01" value={form.value} onChange={(e) => setForm((f) => ({ ...f, value: e.target.value }))} className="lk-input text-sm" placeholder={form.discount_type === "percent" ? "20" : "50"} data-testid="promo-value-input" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-[#D4AF37] mb-1.5 block">Expires (opt.)</label>
          <input type="date" value={form.expires_at} onChange={(e) => setForm((f) => ({ ...f, expires_at: e.target.value }))} className="lk-input text-sm" data-testid="promo-expiry-input" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-[#D4AF37] mb-1.5 block">Max uses (opt.)</label>
          <input type="number" min="1" value={form.max_uses} onChange={(e) => setForm((f) => ({ ...f, max_uses: e.target.value }))} className="lk-input text-sm" placeholder="∞" data-testid="promo-maxuses-input" />
        </div>
        <button type="submit" disabled={creating} className="lk-btn-primary text-xs disabled:opacity-50" data-testid="promo-create-btn">
          {creating ? "Creating…" : "Create Code"}
        </button>
      </form>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-widest text-zinc-500 border-b border-[#D4AF37]/10">
            <tr>
              <th className="text-left p-4">Code</th>
              <th className="text-left p-4">Discount</th>
              <th className="text-left p-4">Uses</th>
              <th className="text-left p-4">Expires</th>
              <th className="text-left p-4">Status</th>
              <th className="text-right p-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {promos.length === 0 && (
              <tr>
                <td colSpan={6} className="p-10 text-center text-zinc-500">No promo codes yet — create your first above.</td>
              </tr>
            )}
            {promos.map((p) => (
              <tr key={p.id} className="border-b border-[#D4AF37]/5 hover:bg-[#D4AF37]/[0.02]" data-testid={`promo-row-${p.id}`}>
                <td className="p-4 font-mono text-[#D4AF37]">{p.code}</td>
                <td className="p-4 text-zinc-300">{p.discount_type === "percent" ? `${p.value}% off` : `$${Number(p.value).toFixed(2)} off`}</td>
                <td className="p-4 text-zinc-300">{p.uses || 0}{p.max_uses ? ` / ${p.max_uses}` : ""}</td>
                <td className="p-4 text-zinc-400 text-xs">{p.expires_at || "Never"}</td>
                <td className="p-4">
                  <span className={`text-xs uppercase tracking-widest ${p.active ? "text-green-400" : "text-zinc-500"}`}>
                    {p.active ? "Active" : "Disabled"}
                  </span>
                </td>
                <td className="p-4 text-right whitespace-nowrap">
                  <button onClick={() => toggle(p)} className="text-xs uppercase tracking-widest text-[#D4AF37] hover:text-[#F3E5AB] mr-4" data-testid={`toggle-promo-${p.id}`}>
                    {p.active ? "Disable" : "Enable"}
                  </button>
                  <button onClick={() => remove(p)} className="text-xs uppercase tracking-widest text-zinc-500 hover:text-red-400" data-testid={`delete-promo-${p.id}`}>
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, testid }) {
  return (
    <div className="lk-card p-6" data-testid={testid}>
      <Icon className="w-5 h-5 text-[#D4AF37] mb-4" />
      <div className="text-xs uppercase tracking-widest text-zinc-500 mb-1">{label}</div>
      <div className="font-serif text-3xl gold-gradient-text">{value}</div>
    </div>
  );
}
